/**
 * Merging the wholesale and retail catalogs into the dealer's service list.
 *
 * A UNION keyed on the service, not a left-fold over wholesale. The old merge
 * mapped over the wholesale list and looked retail up against it, so a service
 * with no WHOLE price row was ABSENT from the dealer's Work step — silently,
 * with no error and no log.
 *
 * That is not an edge case. Production currently has ZERO marine services with
 * both a RETAIL and a WHOLE row, so every marine service there would simply not
 * exist as far as a dealer could tell, and the empty list would look like a
 * broken page rather than a missing wholesale rate.
 *
 * A missing wholesale price is a data problem the installer must fix. Surfacing
 * it as an unpriced row makes it fixable; hiding the service makes it invisible.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/catalogMerge.test.mjs`. Do not add imports.
 */

/** Prefer master_service_id, fall back to service_id — the old merge matched on either. */
const keyOf = (svc) => String(svc?.master_service_id ?? svc?.service_id ?? '');

export function mergeCatalogs(wholesaleServices, retailServices) {
  const wholesale = Array.isArray(wholesaleServices) ? wholesaleServices : [];
  const retail = Array.isArray(retailServices) ? retailServices : [];

  const retailByKey = new Map(retail.map((s) => [keyOf(s), s]));
  const seen = new Set();

  const merged = wholesale.map((w) => {
    const key = keyOf(w);
    seen.add(key);
    const match = retailByKey.get(key);
    return {
      ...w,
      wholesale_price: w.service_cost,
      retail_price: match ? match.service_cost : w.service_cost,
    };
  });

  // Retail-only rows. `missing_wholesale` lets the UI say so rather than present
  // a retail rate as though the dealer would be charged it.
  for (const rRow of retail) {
    const key = keyOf(rRow);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push({
      ...rRow,
      wholesale_price: null,
      retail_price: rRow.service_cost,
      missing_wholesale: true,
    });
  }

  return merged;
}
